'use client';

import type { ReactNode } from 'react';
import { useClientABSmartlyExperiments } from '../hooks/useClientABSmartlyExperiments';
import { ABTestVariant } from '../utils/variants';

export type DynamicExperimentVariantProps = {
  experimentId: string;
  /**
   * Map of variant name to the node to render for that variant.
   * `control` is required as the fallback used before the client context is
   * ready or when the resolved variant has no matching entry.
   */
  variants: Partial<Record<ABTestVariant, ReactNode>> & { [ABTestVariant.Control]: ReactNode };
};

/**
 * Wrapper for displaying one of several variants based on a client-side,
 * multi-variate ABSmartly experiment (e.g. control/treatment1/treatment2).
 * This allows the input components to remain as Server Components.
 * Before the client context is ready, resolves to {@link ABTestVariant.Control}.
 */
export function DynamicExperimentVariant({
  experimentId,
  variants,
}: DynamicExperimentVariantProps) {
  const { results } = useClientABSmartlyExperiments<[ABTestVariant]>(experimentId);
  const variant = results[0] ?? ABTestVariant.Control;

  return variants[variant] ?? variants[ABTestVariant.Control];
}
